import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReuseAvailability } from '@1po/1po-bff-fe-spec/generated/catalog/references/dh/model/ReuseAvailability';
import { ArrowLeftIcon, ExclamationTriangleIcon, TruckIcon } from 'assets/icons';
import { CopyToClipboardButton } from 'components/CopyToClipboardButton';
import { getSparePartsView } from 'domains/user/User.store';
import { ClientView } from 'domains/user/User.types';
import { theme } from 'styles';
import { BlackButton, Collapse, Flex, Icon, MarginBox, Panel, Text, WhiteButton } from 'UI';
import ViewTabs from 'UI/Tabs/ViewTabs';
import { textFormatter } from 'utils';
import { getShippingDelayMaxHours } from './reuseDelivery';
import { ReuseImageGallery } from './ReuseImageGallery';
import { computeInclVat, computeReusePcl, parseReuseNetPrice } from './reusePricing';
import {
  SAccordionContent,
  SBackButton,
  SDeliveryRow,
  SImagePlaceholder,
  SInfoRow,
  SReferenceRow,
  SReuseProductPage,
} from './ReuseProductPage.styled';

// Opisto reuse-availabilities feature is gated to France only — currency is always EUR.
const REUSE_CURRENCY = 'EUR';

const isPresent = (value: string | number | undefined | null): boolean =>
  value !== undefined && value !== null && value !== '';

// Opisto may send firstRegistrationDate as an ISO datetime; keep the date part only.
const formatDate = (value: string | undefined): string | undefined => value?.split('T')[0];

interface InfoRow {
  key: string;
  testId: string;
  label: string;
  value: string | number | undefined | null;
}

interface ReuseProductPageProps {
  part: ReuseAvailability;
  referenceNumber: string;
  referenceName: string | undefined;
  category?: string;
  vatRate: number;
  newPcl: number | undefined;
  newGaragePrice: number | undefined;
  onBack: () => void;
}

const ReuseProductPage = ({
  part,
  referenceNumber,
  referenceName,
  category,
  vatRate,
  newPcl,
  newGaragePrice,
  onBack,
}: ReuseProductPageProps) => {
  const { t } = useTranslation();
  const view = useSelector(getSparePartsView);

  const images = part.images ?? [];
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const netPrice = parseReuseNetPrice(part.netPrice);
  const deliveryFee = isPresent(part.deliveryFee) ? Number(part.deliveryFee) || 0 : undefined;

  const garagePriceVatIncluded = useMemo(() => computeInclVat(netPrice, vatRate), [netPrice, vatRate]);
  const clientPclVatExcluded = useMemo(
    () => (netPrice === undefined ? undefined : computeReusePcl(netPrice, newPcl, newGaragePrice)),
    [netPrice, newPcl, newGaragePrice],
  );
  const clientPclVatIncluded = useMemo(() => computeInclVat(clientPclVatExcluded, vatRate), [
    clientPclVatExcluded,
    vatRate,
  ]);

  const isClientView = view === ClientView;
  const priceExclVat = isClientView ? clientPclVatExcluded : netPrice;
  const priceInclVat = isClientView ? clientPclVatIncluded : garagePriceVatIncluded;

  const characteristicsRows: InfoRow[] = [
    {
      key: 'designation',
      testId: 'reuse-product-page-char-designation',
      label: t('catalog.reuse_availabilities.product_page.characteristics.designation', 'Designation'),
      value: referenceName,
    },
    {
      key: 'category',
      testId: 'reuse-product-page-char-category',
      label: t('catalog.reuse_availabilities.product_page.characteristics.category', 'Category'),
      value: category,
    },
    {
      key: 'condition',
      testId: 'reuse-product-page-char-condition',
      label: t('catalog.reuse_availabilities.card.condition', 'Condition'),
      value: part.condition,
    },
    {
      key: 'quantity',
      testId: 'reuse-product-page-char-quantity',
      label: t('catalog.reuse_availabilities.product_page.characteristics.quantity', 'Quantity'),
      value: 1,
    },
    {
      key: 'warranty',
      testId: 'reuse-product-page-char-warranty',
      label: t('catalog.reuse_availabilities.card.warranty', 'Warranty'),
      value: isPresent(part.warrantyMonth)
        ? t('catalog.reuse_availabilities.card.warrantyMonths', '{{months}} months', { months: part.warrantyMonth })
        : undefined,
    },
  ].filter((row) => isPresent(row.value));

  const vehicleRows: InfoRow[] = [
    {
      key: 'brand',
      testId: 'reuse-product-page-vehicle-brand',
      label: t('catalog.reuse_availabilities.product_page.original_vehicle.brand', 'Brand'),
      value: part.makeLabel,
    },
    {
      key: 'family',
      testId: 'reuse-product-page-vehicle-family',
      label: t('catalog.reuse_availabilities.product_page.original_vehicle.family', 'Family'),
      value: part.modelLabel,
    },
    {
      key: 'version',
      testId: 'reuse-product-page-vehicle-version',
      label: t('catalog.reuse_availabilities.product_page.original_vehicle.version', 'Version'),
      value: part.versionLabel,
    },
    {
      key: 'first_registration_date',
      testId: 'reuse-product-page-vehicle-first-registration-date',
      label: t(
        'catalog.reuse_availabilities.product_page.original_vehicle.first_registration_date',
        'First registration date',
      ),
      value: formatDate(part.firstRegistrationDate),
    },
    {
      key: 'mileage',
      testId: 'reuse-product-page-vehicle-mileage',
      label: t('catalog.reuse_availabilities.card.mileage', 'Mileage'),
      value: part.mileage,
    },
    {
      key: 'color',
      testId: 'reuse-product-page-vehicle-color',
      label: t('catalog.reuse_availabilities.card.color', 'Color'),
      value: part.color,
    },
    {
      key: 'power',
      testId: 'reuse-product-page-vehicle-power',
      label: t('catalog.reuse_availabilities.product_page.original_vehicle.power', 'Power'),
      value: part.power,
    },
    {
      key: 'gearbox',
      testId: 'reuse-product-page-vehicle-gearbox',
      label: t('catalog.reuse_availabilities.product_page.original_vehicle.gearbox', 'Gearbox'),
      value: part.gearbox,
    },
    {
      key: 'engine_code',
      testId: 'reuse-product-page-vehicle-engine-code',
      label: t('catalog.reuse_availabilities.product_page.original_vehicle.engine_code', 'Engine code'),
      value: part.engineCode,
    },
    {
      key: 'gearbox_code',
      testId: 'reuse-product-page-vehicle-gearbox-code',
      label: t('catalog.reuse_availabilities.product_page.original_vehicle.gearbox_code', 'Gearbox code'),
      value: part.gearboxCode,
    },
    {
      key: 'vin',
      testId: 'reuse-product-page-vehicle-vin',
      label: t('catalog.reuse_availabilities.product_page.original_vehicle.vin', 'VIN'),
      value: part.vin,
    },
    {
      key: 'number_of_doors',
      testId: 'reuse-product-page-vehicle-number-of-doors',
      label: t('catalog.reuse_availabilities.card.number_of_doors', 'Number of doors'),
      value: part.numberOfDoors,
    },
  ].filter((row) => isPresent(row.value));

  const renderRow = (row: InfoRow) => (
    <SInfoRow key={row.key} data-testid={row.testId}>
      <Text type={'base'} bold color={'brand_black_light'}>{`${row.label}:`}</Text>
      <Text type={'base'} color={'grey00_light'}>{`${row.value}`}</Text>
    </SInfoRow>
  );

  const renderDeliveryFee = () =>
    deliveryFee !== undefined ? (
      <Text type={'md'} color={'palette_green_7'} data-testid={'reuse-product-page-delivery-fee'}>
        {t('catalog.reuse_availabilities.product_page.delivery_from', ' - Delivery from {{fee}}', {
          fee: textFormatter.formatCurrency(deliveryFee, REUSE_CURRENCY),
        })}
      </Text>
    ) : null;

  const renderDelivery = () =>
    isPresent(part.shippingDelayMax) ? (
      <SDeliveryRow data-testid={'reuse-product-page-delivery-time'}>
        <Icon IconComponent={TruckIcon} size={22} color={theme.color.palette_green_7} />
        <Text type={'md'} color={'palette_green_7'}>
          {t('catalog.reuse_availabilities.card.delivery_time', 'Delivered under {{hours}} hours', {
            hours: getShippingDelayMaxHours(part.shippingDelayMax),
          })}
        </Text>
        {renderDeliveryFee()}
      </SDeliveryRow>
    ) : (
      <SDeliveryRow data-testid={'reuse-product-page-delivery-unavailable'}>
        <Icon IconComponent={TruckIcon} size={22} color={theme.color.palette_green_7} />
        <Icon IconComponent={ExclamationTriangleIcon} size={16} color={theme.color.grey55} />
        <Text type={'md'} color={'grey55'}>
          <Trans i18nKey={'catalog.reuse_availabilities.card.delivery_unavailable'}>
            {'Delivery date: Unavailable'}
          </Trans>
        </Text>
        {renderDeliveryFee()}
      </SDeliveryRow>
    );

  return (
    <SReuseProductPage data-testid={'reuse-product-page'}>
      <Flex direction={'row'} justify={'space-between'} align={'center'}>
        <SBackButton type={'button'} data-testid={'reuse-product-page-back'} onClick={onBack}>
          <Icon IconComponent={ArrowLeftIcon} size={16} />
          <Text type={'base'} cursor={'pointer'} disableGutter noWrap>
            {t('catalog.reuse_availabilities.product_page.back_to_list', 'Back to result list')}
          </Text>
        </SBackButton>
        <ViewTabs />
      </Flex>
      <Flex direction={['column', 'row']} gap={40}>
        <ReuseImageGallery
          images={images}
          width={'100%'}
          height={400}
          imageTestId={'reuse-product-page-image'}
          mainImageTestId={'reuse-product-page-main-image'}
          placeholderTestId={'reuse-product-page-image-placeholder'}
          placeholder={
            <SImagePlaceholder data-testid={'reuse-product-page-image-placeholder'}>
              <Text type={'light_12_black_65'}>
                {t('catalog.reuse_availabilities.card.no_image', 'No image available')}
              </Text>
            </SImagePlaceholder>
          }
          showThumbnails
          thumbnailTestId={'reuse-product-page-thumbnail'}
          thumbnailStripTestId={'reuse-product-page-thumbnails'}
          selectedIndex={selectedImageIndex}
          onSelectIndex={setSelectedImageIndex}
        />
        <Flex direction={'column'} gap={16} minWidth={350}>
          <SReferenceRow>
            <Text type={'base'} color={'grey35'} disableGutter data-testid={'reuse-product-page-reference-number'}>
              {referenceNumber}
            </Text>
            <CopyToClipboardButton textToCopy={referenceNumber} size={16} />
          </SReferenceRow>
          <Text type={'heading2'} disableGutter data-testid={'reuse-product-page-designation'}>
            {referenceName}
          </Text>
          <Flex direction={'column'} gap={5} maxWidth={350} size={0}>
            {priceExclVat === undefined || priceInclVat === undefined ? (
              <Text type={'h5_bold'} data-testid={'reuse-product-page-price-unavailable'}>
                {t('common.price.unavailable', 'Price unavailable')}
              </Text>
            ) : (
              <>
                <Text type={'h5_bold'} data-testid={'reuse-product-page-price-excl-vat'}>
                  {t('common.price.vat_exclude_price', '{{vat_exclude_price}} VAT. Excl', {
                    vat_exclude_price: textFormatter.formatCurrency(priceExclVat, REUSE_CURRENCY),
                  })}
                </Text>
                <Text type={'light_12_black_65'} data-testid={'reuse-product-page-price-incl-vat'}>
                  {t('common.price.or_vat_include_price', 'or {{vat_include_price}} VAT. Incl', {
                    vat_include_price: textFormatter.formatCurrency(priceInclVat, REUSE_CURRENCY),
                  })}
                </Text>
              </>
            )}
          </Flex>
          {renderDelivery()}
          <Flex direction={'column'} gap={10} size={0} minWidth={[0, 350]} maxWidth={['100%', 350]}>
            <BlackButton disabled data-cy={'reuse-product-page-add-to-cart'}>
              {t('cart.action.add_to_cart', 'Add to cart')}
            </BlackButton>
            <WhiteButton disabled data-cy={'reuse-product-page-add-to-estimate'}>
              {t('catalog.action.add_to_estimate', 'Add to estimate')}
            </WhiteButton>
          </Flex>
        </Flex>
      </Flex>

      <MarginBox mt={15}>
        <Collapse defaultActiveKey={['1']} position={'end'} noBorder>
          <Panel
            header={t('catalog.reuse_availabilities.product_page.characteristics.title', 'Characteristics')}
            key={'1'}
          >
            <SAccordionContent data-testid={'reuse-product-page-characteristics'}>
              {characteristicsRows.map((row) => renderRow(row))}
            </SAccordionContent>
          </Panel>
        </Collapse>
        <Collapse defaultActiveKey={['1']} position={'end'} noBorder>
          <Panel
            header={t('catalog.reuse_availabilities.product_page.original_vehicle.title', 'Original vehicle')}
            key={'1'}
          >
            <SAccordionContent data-testid={'reuse-product-page-original-vehicle'}>
              {vehicleRows.map((row) => renderRow(row))}
            </SAccordionContent>
          </Panel>
        </Collapse>
      </MarginBox>
    </SReuseProductPage>
  );
};

export default ReuseProductPage;
